import { useEffect, useState, type ReactElement } from 'react';
import { Skeleton } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { EntityDetailLayout } from '../../../components/entity-detail/EntityDetailLayout.js';
import { EntityHistoryFeed } from '../../../components/entity-detail/EntityHistoryFeed.js';
import { EntityTabs } from '../../../components/entity-detail/EntityTabs.js';
import { ErrorBoundary } from '../../../shared/ErrorBoundary.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { describeActivity } from '../../activity/lib/describeActivity.js';
import { useEntityActivities } from '../../activity/hooks/useEntityActivities.js';
import { EntityAttachmentsPanel } from '../../attachments/components/EntityAttachmentsPanel.js';
import { EntityNotesPanel } from '../../notes/components/EntityNotesPanel.js';
import { useNotes } from '../../notes/hooks/useNotes.js';
import { MeetingFormModal } from '../components/MeetingFormModal.js';
import { MeetingHeader } from '../components/MeetingHeader.js';
import { MeetingOverview } from '../components/MeetingOverview.js';
import { MeetingSidebar } from '../components/MeetingSidebar.js';
import { useMeeting } from '../hooks/useMeetings.js';

/**
 * Карточка встречи на общем стандарте entity-detail: шапка с действиями,
 * сайдбар со сводкой/связями/участниками и вкладки с синхронизацией `?tab=`.
 * Вкладки: Обзор, Файлы, Заметки, История (история раньше открывалась дровером —
 * тот же список событий, теперь во вкладке рядом с остальными).
 */
export function MeetingCardPage({ meetingId }: { meetingId: string }): ReactElement {
  const { t } = useTranslation();
  const [formOpen, setFormOpen] = useState(false);
  const query = useMeeting(meetingId);
  const notes = useNotes({ calendarEventId: meetingId });
  const history = useEntityActivities('meeting', meetingId);
  useEffect(() => {
    setFormOpen(false);
  }, [meetingId]);
  if (query.isLoading)
    return (
      <main className="p-6">
        <Skeleton active />
      </main>
    );
  if (query.isError || !query.data)
    return (
      <main className="p-6">
        <ErrorState
          title={t('crm.features.features.meetings.pages.meetingcardpage.встреча_не_найдена')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </main>
    );
  const meeting = query.data;
  const activities = history.data ?? [];
  const historyItems = activities.map((item) => ({
    id: item.id,
    text: describeActivity(item),
    date: item.occurredAt ?? item.createdAt,
  }));
  const historyContent = history.isLoading ? (
    <Skeleton active />
  ) : history.error ? (
    <ErrorState
      title={t('crm.entity_detail.load_error')}
      error={history.error}
      onRetry={() => void history.refetch()}
    />
  ) : (
    <EntityHistoryFeed items={historyItems} />
  );
  return (
    <ErrorBoundary>
      <EntityDetailLayout
        header={<MeetingHeader meeting={meeting} onEdit={() => setFormOpen(true)} />}
        sidebar={<MeetingSidebar meeting={meeting} />}
        main={
          <EntityTabs
            items={[
              {
                key: 'overview',
                label: t('crm.entity_detail.overview'),
                content: <MeetingOverview meeting={meeting} history={activities} />,
              },
              {
                key: 'files',
                label: t('crm.entity_detail.files'),
                content: <EntityAttachmentsPanel entity="meeting" entityId={meeting.id} />,
              },
              {
                key: 'notes',
                label: t('crm.entity_detail.notes'),
                count: notes.data?.length ?? 0,
                content: (
                  <EntityNotesPanel
                    calendarEventId={meeting.id}
                    title={t(
                      'crm.features.features.meetings.pages.meetingcardpage.заметки_по_встрече',
                    )}
                  />
                ),
              },
              {
                key: 'history',
                label: t('crm.entity_detail.history'),
                count: historyItems.length,
                content: historyContent,
              },
            ]}
          />
        }
      />
      {formOpen ? (
        <MeetingFormModal open onClose={() => setFormOpen(false)} meeting={meeting} />
      ) : null}
    </ErrorBoundary>
  );
}
